import React, { useEffect, useRef } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCIcon, RCCTooltip } from "react-common-components";
import { useFieldArray } from "react-hook-form";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import SortableContainer from "src/components/Sortable/Container";
import SortableItem from "src/components/Sortable/Item";
import IndicatorModal from "src/components/ModalIndicator";
import { IComponentIndicatorTab, IIndicator } from "./interface";
import { IIndicatorForm, IRefIndicatorModal } from "../ModalIndicator/interface";
import IndicatorItem from "./indicatorItem";
import { restrictToVerticalAxis } from "../Sortable/Container/modifiers";

const AssociateIndicators: React.FC<IComponentIndicatorTab> = ({
	control,
	error,
	indicatorsDefaultValue,
	disabled = false,
	order = false,
	register,
	unregister,
	clearErrors,
	names
}) => {
	const { translate } = useTranslation();

	const refModal = useRef<IRefIndicatorModal>(null);

	const { 
		fields: fieldsIndicators, 
		remove: removeIndicators,
		append: appendIndicators,
		move: moveIndicators
	} = useFieldArray({
		control,
		name: names.nameOfObject
	});

	const addIndicator = () => {
		clearErrors(names.nameOfObject);
		const indicator: IIndicator = {};
		indicator.key = v4();
		indicator.id = order ? v4() : "0";
		indicator.iqc001Id = undefined;
		indicator.peso = 1;

		appendIndicators(indicator);
	};

	const removeIndicator = (index: number) => {
		clearErrors(names.nameOfObject);			
		removeIndicators(index);
	};

	const handleDragEnd = (activeId: string, overId: string) => {
		const activeIndex = fieldsIndicators.findIndex((checks) => checks.id === activeId);
		const overIndex = fieldsIndicators.findIndex((checks) => checks.id === overId);
		moveIndicators(activeIndex, overIndex);
		unregister(names.nameOfObject);
	};

	useEffect(() => {
		appendIndicators(indicatorsDefaultValue.map((indicator) => ({
			id: indicator[names.nameOfID],
			iqc001Id: indicator[names.nameOfSelectIndicator],
			peso: indicator[names.nameOfRangeSlider],
			checkbox: names.nameOfCheckbox ? indicator[names.nameOfCheckbox] : undefined,
			key: v4()
		})));
	}, []);

	const callbackAddIndicator = (indicatorX: IIndicatorForm ) => {
		const indicator: IIndicator = {};
		indicator.key = v4();
		indicator.id = v4();
		indicator.iqc001Id = indicatorX.indicator.iqc001Id;
		indicator.peso = 1;

		appendIndicators(indicator);
	};

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
				justifyContent="center"
			>
				<RCCGrid
					container
					item
				>
					<RCCGrid
						container
						item
						justifyContent="flex-end"
					>
						<RCCTooltip
							color="purple"
							text={translate("Adicionar Indicador")}
						>
							<RCCButton
								color="purple"
								icon={{
									name: "plus"
								}}
								layout="isRounded"
								size="md"
								onClick={addIndicator}
								disabled={disabled}
							/>
						</RCCTooltip>
						<RCCTooltip
							text={translate("Novo indicador")}
							color="grey"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="grey"
								layout="isRounded"
								onClick={() => { refModal?.current?.actionButtonAdd((newIndicator) => newIndicator !== undefined ? callbackAddIndicator(newIndicator) : undefined);}}
								size="md"
							/>
						</RCCTooltip>
					
					</RCCGrid>
				</RCCGrid>
								
				<RCCGrid
					container
					item
				>
					{error[names.nameOfObject] && error[names.nameOfObject].message && (
						<Title color="red" size="xs">
							<RCCIcon
								name="infoCircle"
								color="var(--red)"
								style={{ marginRight: "8px"}}
							/>
							{error[names.nameOfObject].message}
						</Title>
					)}
					{fieldsIndicators.length === 0 && (
						<RCCAlert
							title={translate("Nenhum indicador adicionado")}
							description={translate("Aperte no botão acima para adicionar")}
						/>
					)}
					<SortableContainer 
						items={fieldsIndicators}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToVerticalAxis]}
					>
						{fieldsIndicators.map((indicator: any, index) => (
							<SortableItem id={indicator.id} key={indicator.key ? indicator.key : `indicator-${indicator.key}`}>
								<IndicatorItem 
									removeIndicator={removeIndicator}
									register={register}
									clearErrors={clearErrors}
									control={control}
									indicator={indicator}
									disabled={disabled}
									order={order}
									error={error}
									index={index}
									names={names}
								/>
							</SortableItem>
						))}
					</SortableContainer>
				</RCCGrid>		
			</RCCGrid>
			<IndicatorModal ref={refModal} /> 
		</>
	);
};

export default AssociateIndicators;
